/**
 * One leave request, read only, with its supporting documents.
 *
 * Reachable from every row of Leave Approvals and Leave Administration at any
 * status. A medical certificate has to be readable BEFORE the decision is
 * taken, and for a request still sitting with the line manager - or already
 * decided - there was previously no way to open it at all, because the
 * attachment was only rendered inside the Approve/Reject dialog.
 */
import React from 'react';
import { format } from 'date-fns';
import { CalendarDays, CheckCircle, Clock, Trash2, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { LeaveAttachmentList } from '@/components/leave/LeaveAttachments';
import { classifyManagerAction, classifyHrAction } from '@/lib/leaveDecisionClassifier';
import { leaveDisplayStatus } from '@/lib/requestLifecycle';

const STATUS_STYLES = {
  pending_manager: { bg: 'bg-amber-100 text-amber-800', icon: Clock, en: 'Pending Manager', ar: 'بانتظار المدير' },
  pending_hr: { bg: 'bg-blue-100 text-blue-800', icon: Clock, en: 'Pending HR', ar: 'بانتظار الموارد البشرية' },
  approved: { bg: 'bg-green-100 text-green-800', icon: CheckCircle, en: 'Approved', ar: 'موافق عليه' },
  rejected: { bg: 'bg-red-100 text-red-800', icon: XCircle, en: 'Rejected', ar: 'مرفوض' },
  cancelled: { bg: 'bg-slate-100 text-slate-700', icon: XCircle, en: 'Cancelled', ar: 'ملغي' },
  deleted_by_manager: { bg: 'bg-rose-100 text-rose-800', icon: Trash2, en: 'Deleted by manager', ar: 'حذفه المدير' },
  deleted: { bg: 'bg-rose-100 text-rose-800', icon: Trash2, en: 'Deleted by HR', ar: 'حذفته الموارد البشرية' },
};

const TONE_BG = {
  green: 'bg-green-50 text-green-700',
  red: 'bg-red-50 text-red-700',
  amber: 'bg-amber-50 text-amber-700',
  slate: 'bg-slate-50 text-slate-700',
};

const asDate = (value, pattern) => {
  if (!value) return '-';
  try { return format(new Date(value), pattern); } catch { return String(value); }
};

const Field = ({ label, value, testid }) => (
  <div className="flex items-start justify-between gap-4 text-sm">
    <span className="text-slate-600">{label}</span>
    <span className="font-medium text-end" data-testid={testid}>{value}</span>
  </div>
);

const Decision = ({ title, decision, isRTL, testid }) => {
  if (!decision || decision.kind === 'none') return null;
  const sub = isRTL ? decision.sub_ar : decision.sub;
  const actor = decision.tooltip?.actor_name?.trim();
  const when = decision.tooltip?.timestamp ? asDate(decision.tooltip.timestamp, 'PPp') : null;

  return (
    <div className="space-y-1.5" data-testid={testid}>
      <h4 className="text-sm font-medium">{title}</h4>
      <div className={cn('rounded p-3 text-sm', TONE_BG[decision.tone] || TONE_BG.slate)}>
        <p className="font-semibold">
          {isRTL ? decision.label_ar : decision.label}{sub ? ` · ${sub}` : ''}
        </p>
        {actor && (
          <p className="mt-1 text-xs opacity-80">{isRTL ? 'بواسطة: ' : 'By: '}{actor}</p>
        )}
        {when && <p className="text-xs opacity-80">{isRTL ? 'الوقت: ' : 'At: '}{when}</p>}
        {decision.tooltip?.reason && <p className="mt-1">{decision.tooltip.reason}</p>}
      </div>
    </div>
  );
};

export const LeaveRequestDetailsDialog = ({
  open, onClose, request, token, isRTL, language, footer,
}) => {
  if (!request) return null;

  const displayStatus = leaveDisplayStatus(request);
  const status = STATUS_STYLES[displayStatus];
  const StatusIcon = status?.icon;
  const leaveType = (language === 'ar' && request.leave_type_name_ar)
    ? request.leave_type_name_ar
    : request.leave_type_name;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="leave-details-dialog">
        <DialogHeader>
          <DialogTitle>{leaveType || (isRTL ? 'طلب إجازة' : 'Leave request')}</DialogTitle>
          <DialogDescription>
            {request.employee_name?.trim() || (isRTL ? 'غير معروف' : 'Unknown')}
            {request.employee_department ? ` — ${request.employee_department}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">{isRTL ? 'الحالة' : 'Status'}</span>
            {status ? (
              <Badge className={status.bg} data-testid="leave-details-status">
                {StatusIcon && <StatusIcon className="h-3 w-3 me-1" />}
                {isRTL ? status.ar : status.en}
              </Badge>
            ) : (
              <span className="text-sm font-medium" data-testid="leave-details-status">{request.status}</span>
            )}
          </div>

          <div className="rounded border bg-slate-50 p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <CalendarDays className="h-4 w-4 text-slate-500" />
              {asDate(request.start_date, 'dd MMM yyyy')} → {asDate(request.end_date, 'dd MMM yyyy')}
            </div>
            <Field
              label={isRTL ? 'أيام الإجازة' : 'Leave days'}
              value={request.working_days_requested ?? '-'}
              testid="leave-details-days"
            />
            <Field
              label={isRTL ? 'تاريخ التقديم' : 'Submitted'}
              value={asDate(request.submitted_at || request.created_at, 'dd MMM yyyy')}
              testid="leave-details-submitted"
            />
          </div>

          {request.reason && (
            <div className="space-y-1.5">
              <h4 className="text-sm font-medium">{isRTL ? 'سبب الموظف' : "Employee's reason"}</h4>
              <p
                className="text-sm text-slate-700 bg-slate-50 border rounded p-3 whitespace-pre-wrap"
                data-testid="leave-details-reason"
              >
                {request.reason}
              </p>
            </div>
          )}

          {request.archive_action === 'delete' && request.archive_reason && (
            <div className="space-y-1.5">
              <h4 className="text-sm font-medium">{isRTL ? 'سبب الحذف' : 'Reason for deleting'}</h4>
              <p
                className="text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded p-3 whitespace-pre-wrap"
                data-testid="leave-details-delete-reason"
              >
                {request.archive_reason}
              </p>
            </div>
          )}

          <LeaveAttachmentList
            token={token}
            requestId={request.id}
            isRTL={isRTL}
            showEmpty
          />

          <Decision
            title={isRTL ? 'قرار المدير' : 'Manager decision'}
            decision={classifyManagerAction(request)}
            isRTL={isRTL}
            testid="leave-details-manager-decision"
          />
          <Decision
            title={isRTL ? 'قرار الموارد البشرية' : 'HR decision'}
            decision={classifyHrAction(request)}
            isRTL={isRTL}
            testid="leave-details-hr-decision"
          />
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose} data-testid="leave-details-close">
            {isRTL ? 'إغلاق' : 'Close'}
          </Button>
          {footer}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LeaveRequestDetailsDialog;
